import React from 'react';
import { useNavigate } from 'react-router-dom';
import SignalBadge from './SignalBadge';

const formatNum = (v, dec = 2) => (v == null || Number.isNaN(v) ? '—' : Number(v).toFixed(dec));
const colorPct = (v) =>
  v == null ? 'text-gray-400'
    : v > 0 ? 'text-green-600 dark:text-green-400'
    : v < 0 ? 'text-red-500 dark:text-red-400'
    : 'text-gray-600 dark:text-gray-400';

const COLUMNS = [
  { key: 'symbol', label: 'Symbol', sortable: true, width: 'w-20' },
  { key: 'name', label: 'Name', width: 'w-48' },
  { key: 'sector', label: 'Sector', width: 'w-32' },
  { key: 'close', label: 'Close', align: 'right' },
  { key: 'pct', label: 'Δ %', align: 'right' },
  { key: 'signal', label: 'Signal', sortable: true, align: 'center' },
  { key: 'score', label: 'Score', sortable: true, align: 'right' },
  { key: 'rsi', label: 'RSI', align: 'right' },
  { key: 'macd', label: 'MACD H', align: 'right' },
  { key: 'bb', label: 'BB %', align: 'right' },
  { key: 'adx', label: 'ADX', align: 'right' },
];

const ScannerTable = ({ rows = [], sort, onSortChange }) => {
  const navigate = useNavigate();

  const toggleSort = (key) => {
    if (!onSortChange) return;
    const map = { symbol: 'symbol', signal: 'signal', score: 'score' };
    const dbKey = map[key];
    if (!dbKey) return;
    const next = sort === `-${dbKey}` ? dbKey : `-${dbKey}`;
    onSortChange(next);
  };

  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full text-xs md:text-sm border-collapse">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
            {COLUMNS.map((col) => {
              const active = col.sortable && (sort === col.key || sort === `-${col.key}`);
              return (
                <th
                  key={col.key}
                  className={`px-2 py-2 whitespace-nowrap ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''} ${col.sortable ? 'cursor-pointer select-none hover:text-[#c9a96e]' : ''} ${active ? 'text-[#c9a96e]' : ''}`}
                  onClick={() => col.sortable && toggleSort(col.key)}
                >
                  {col.label}
                  {active && (sort.startsWith('-') ? ' ↓' : ' ↑')}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.symbol}
              onClick={() => navigate(`/signals/symbol/${r.symbol}`)}
              className="border-b border-gray-100 dark:border-gray-900 hover:bg-gray-50 dark:hover:bg-gray-900/50 cursor-pointer"
            >
              <td className="px-2 py-2 font-semibold text-[#c9a96e]">{r.symbol}</td>
              <td className="px-2 py-2 text-gray-700 dark:text-gray-300 truncate max-w-[12rem]" title={r.name}>{r.name}</td>
              <td className="px-2 py-2 text-gray-500 dark:text-gray-400 truncate max-w-[8rem]" title={r.sector}>{r.sector || '—'}</td>
              <td className="px-2 py-2 text-right text-gray-900 dark:text-gray-100">{formatNum(r.close, 2)}</td>
              <td className={`px-2 py-2 text-right ${colorPct(r.pct_change)}`}>
                {r.pct_change == null ? '—' : `${r.pct_change >= 0 ? '+' : ''}${formatNum(r.pct_change, 2)}%`}
              </td>
              <td className="px-2 py-2 text-center"><SignalBadge signal={r.signal} /></td>
              <td className={`px-2 py-2 text-right font-semibold ${colorPct(Number(r.score))}`}>
                {Number(r.score).toFixed(2)}
              </td>
              <td className="px-2 py-2 text-right text-gray-700 dark:text-gray-300">
                {formatNum(r.indicators?.rsi_14, 1)}
              </td>
              <td className={`px-2 py-2 text-right ${colorPct(r.indicators?.macd_hist)}`}>
                {formatNum(r.indicators?.macd_hist, 3)}
              </td>
              <td className="px-2 py-2 text-right text-gray-700 dark:text-gray-300">
                {formatNum(r.indicators?.bb_pct, 2)}
              </td>
              <td className="px-2 py-2 text-right text-gray-700 dark:text-gray-300">
                {formatNum(r.indicators?.adx, 1)}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={COLUMNS.length} className="text-center py-12 text-gray-400">
                No symbols match the current filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ScannerTable;
